// --- src/data/m2/Unit5.js ---

export const M2_UNIT_5 = {
  id: "m2-u5-there-is-are",
  title: "Unit 5: There is/are & Quantifiers",
  topic: "Describing Places & Existence",
  iconType: "map",
  grammarImage: "/m2-u5-there-is-are.jpg",
  grammarText: "Aprende a describir lugares y hablar de existencia usando There is/are con Some (afirmativo), Any (negativo/pregunta) y A lot of (cantidad grande).",
  sets: [

    // ===================================================================
    // SET 1: THERE IS/ARE BASICS (A1)
    // ===================================================================
    {
      id: "m2-u5-set1",
      title: "Set 1: There is / There are",
      questions: [
        {
          type: "mc",
          q: "'_____ a bank on Main Street.'",
          options: ["There is", "There are"],
          a: "There is",
          tip: "A bank = singular → There IS."
        },
        {
          type: "mc",
          q: "'_____ three restaurants here.'",
          options: ["There are", "There is"],
          a: "There are",
          tip: "Three restaurants = plural → There ARE.",
          successtip: "There is = singular, There are = plural! Easy! 🎯"
        },
        {
          type: "input",
          q: "Complete: '_____ _____ a park near my house.' (2 words)",
          a: "There is",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["there's"] },
          tip: "A park = one thing = singular."
        },
        {
          type: "match",
          q: "Match the sentence to the correct form.",
          pairs: [
            { left: "One hotel", right: "There is a hotel" },
            { left: "Many hotels", right: "There are hotels" },
            { left: "No hotel", right: "There isn't a hotel" }
          ],
          a: "completed",
          successtip: "Singular=is, Plural=are, Negative=isn't/aren't! 💡"
        },
        {
          type: "order",
          q: "Order the words.",
          words: ["There", "are", "two", "bathrooms."],
          a: "There are two bathrooms.",
          tip: "Two = plural → are."
        },
        {
          type: "mc",
          q: "Negative form: '_____ any supermarkets here.'",
          options: ["There aren't", "There isn't"],
          a: "There aren't",
          tip: "Supermarkets = plural → aren't."
        },
        {
          type: "input",
          q: "Complete: 'Is _____ a bank?' (1 word)",
          a: "there",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "Is there...? = question form! ❓"
        },
        {
          type: "order",
          q: "Order the question.",
          words: ["Are", "there", "any", "restaurants?"],
          a: "Are there any restaurants?",
          tip: "Question: Are there...?"
        },
        {
          type: "match",
          q: "Match the question to the answer.",
          pairs: [
            { left: "Is there a park?", right: "Yes, there is." },
            { left: "Are there shops?", right: "No, there aren't." },
            { left: "Is there wifi?", right: "Yes, there is." }
          ],
          a: "completed",
          tip: "Short answers: Yes/No, there is/isn't/are/aren't."
        },
        {
          type: "mc",
          q: "'_____ many students in the class.'",
          options: ["There are", "There is"],
          a: "There are",
          tip: "Many students = plural."
        }
      ]
    },

    // ===================================================================
    // SET 2: SOME vs ANY (A1+)
    // ===================================================================
    {
      id: "m2-u5-set2",
      title: "Set 2: Some vs Any",
      questions: [
        {
          type: "mc",
          q: "Positive: 'There are ___ apples in the kitchen.'",
          options: ["some", "any"],
          a: "some",
          tip: "Positive statement → SOME.",
          successtip: "SOME = affirmative (+) sentences! 🍎"
        },
        {
          type: "mc",
          q: "Negative: 'There aren't ___ banks here.'",
          options: ["any", "some"],
          a: "any",
          tip: "Negative statement → ANY."
        },
        {
          type: "input",
          q: "Question: 'Are there ___ good restaurants?'",
          a: "any",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "ANY = questions (?) and negatives (-)! 🎯"
        },
        {
          type: "match",
          q: "Match the rule.",
          pairs: [
            { left: "Positive (+)", right: "There are SOME..." },
            { left: "Negative (-)", right: "There aren't ANY..." },
            { left: "Question (?)", right: "Are there ANY...?" }
          ],
          a: "completed",
          tip: "SOME = +, ANY = - and ?"
        },
        {
          type: "order",
          q: "Order the sentence.",
          words: ["There", "are", "some", "chairs."],
          a: "There are some chairs.",
          tip: "Positive → some."
        },
        {
          type: "mc",
          q: "'There isn't ___ water.'",
          options: ["any", "some"],
          a: "any",
          tip: "Negative → any."
        },
        {
          type: "input",
          q: "'There are ___ books on the table.' (positive)",
          a: "some",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Order the question.",
          words: ["Is", "there", "any", "milk?"],
          a: "Is there any milk?",
          tip: "Question → any.",
          successtip: "Milk = uncountable → is! 🥛"
        },
        {
          type: "mc",
          q: "'Do you have ___ questions?' (question)",
          options: ["any", "some"],
          a: "any",
          tip: "Questions → any."
        },
        {
          type: "input",
          q: "'I want ___ coffee.' (positive)",
          a: "some",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Positive → some."
        }
      ]
    },

    // ===================================================================
    // SET 3: A LOT OF (A2)
    // ===================================================================
    {
      id: "m2-u5-set3",
      title: "Set 3: A lot of",
      questions: [
        {
          type: "mc",
          q: "'This area is busy. There are ___ cars.'",
          options: ["a lot of", "any", "some"],
          a: "a lot of",
          tip: "Large quantity → A LOT OF.",
          successtip: "A lot of = MUCHOS/MUCHAS! Big quantity! 🚗🚗🚗"
        },
        {
          type: "order",
          q: "Order the sentence.",
          words: ["There", "are", "a", "lot", "of", "people."],
          a: "There are a lot of people.",
          tip: "A lot of = many."
        },
        {
          type: "input",
          q: "'There are ___ ___ ___ windows.' (many windows - 3 words)",
          a: "a lot of",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "match",
          q: "Match quantity expressions.",
          pairs: [
            { left: "Large quantity", right: "A lot of" },
            { left: "Small quantity (+)", right: "Some" },
            { left: "Zero / Question", right: "Any" }
          ],
          a: "completed",
          successtip: "Quantity scale: A lot of > Some > Any (0)! 📊"
        },
        {
          type: "mc",
          q: "'There are ___ students in the cafeteria.' (many)",
          options: ["a lot of", "any"],
          a: "a lot of",
          tip: "Many = a lot of."
        },
        {
          type: "order",
          q: "Order the sentence.",
          words: ["There", "is", "a", "lot", "of", "traffic."],
          a: "There is a lot of traffic.",
          tip: "Traffic = uncountable → is.",
          successtip: "A lot of works with BOTH countable AND uncountable! 🎯"
        },
        {
          type: "input",
          q: "'There are ___ ___ ___ books.' (3 words)",
          a: "a lot of",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "mc",
          q: "'Is there ___ traffic?' (large quantity question)",
          options: ["a lot of", "any", "some"],
          a: "a lot of",
          tip: "You can use 'a lot of' in questions about quantity."
        },
        {
          type: "order",
          q: "Order the sentence.",
          words: ["There", "are", "a", "lot", "of", "trees."],
          a: "There are a lot of trees.",
          tip: "Trees = countable plural."
        },
        {
          type: "input",
          q: "'There is ___ ___ ___ water.' (3 words - large quantity)",
          a: "a lot of",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Water = uncountable."
        }
      ]
    },

    // ===================================================================
    // SET 4: DESCRIBING PLACES (A2)
    // ===================================================================
    {
      id: "m2-u5-set4",
      title: "Set 4: Describing Places",
      questions: [
        {
          type: "match",
          q: "Match the place to what it has.",
          pairs: [
            { left: "Kitchen", right: "There is a fridge" },
            { left: "Bedroom", right: "There is a bed" },
            { left: "Living room", right: "There is a sofa" }
          ],
          a: "completed",
          successtip: "Each room has specific furniture! 🏠"
        },
        {
          type: "mc",
          q: "'In the kitchen, there ___ some plates.'",
          options: ["are", "is"],
          a: "are",
          tip: "Plates = plural."
        },
        {
          type: "order",
          q: "Describe the bathroom.",
          words: ["There", "is", "a", "shower."],
          a: "There is a shower.",
          tip: "A shower = singular."
        },
        {
          type: "input",
          q: "'In the bedroom, there ___ two windows.' (1 word)",
          a: "are",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Two = plural."
        },
        {
          type: "mc",
          q: "'In the living room, there are ___ cushions.'",
          options: ["some", "any"],
          a: "some",
          tip: "Positive → some."
        },
        {
          type: "order",
          q: "Order the sentence.",
          words: ["There", "is", "a", "TV", "in", "the", "room."],
          a: "There is a TV in the room.",
          tip: "A TV = singular."
        },
        {
          type: "input",
          q: "'There aren't ___ chairs.' (negative)",
          a: "any",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "match",
          q: "Match furniture to rooms.",
          pairs: [
            { left: "Stove", right: "Kitchen" },
            { left: "Toilet", right: "Bathroom" },
            { left: "Wardrobe", right: "Bedroom" }
          ],
          a: "completed",
          tip: "Learn room vocabulary!"
        },
        {
          type: "mc",
          q: "'Is there ___ table in the dining room?'",
          options: ["a", "some", "any"],
          a: "a",
          tip: "Singular countable → a.",
          successtip: "Use A/AN for singular items! 🪑"
        },
        {
          type: "order",
          q: "Order the sentence.",
          words: ["There", "are", "some", "books", "on", "the", "shelf."],
          a: "There are some books on the shelf.",
          tip: "Positive plural → some."
        }
      ]
    },

    // ===================================================================
    // SET 5: QUESTIONS & SHORT ANSWERS (A2)
    // ===================================================================
    {
      id: "m2-u5-set5",
      title: "Set 5: Questions & Answers",
      questions: [
        {
          type: "match",
          q: "Match the question to the answer.",
          pairs: [
            { left: "Is there a park?", right: "Yes, there is." },
            { left: "Are there shops?", right: "No, there aren't." },
            { left: "Is there wifi?", right: "Yes, there is." }
          ],
          a: "completed",
          successtip: "Short answers: Yes/No + there + is/isn't/are/aren't! ✅"
        },
        {
          type: "mc",
          q: "'Is there a bank near here?' '___'",
          options: ["Yes, there is.", "Yes, it is.", "Yes, there are."],
          a: "Yes, there is.",
          tip: "Answer matches question form."
        },
        {
          type: "order",
          q: "Order the question.",
          words: ["Are", "there", "any", "restaurants?"],
          a: "Are there any restaurants?",
          tip: "Question → any."
        },
        {
          type: "input",
          q: "'Are there any hotels?' 'No, there _____.' (2 words)",
          a: "aren't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["are not"] }
        },
        {
          type: "mc",
          q: "'Is there a swimming pool?' '___'",
          options: ["No, there isn't.", "No, there aren't.", "No, it isn't."],
          a: "No, there isn't.",
          tip: "Singular question → isn't."
        },
        {
          type: "order",
          q: "Order the question.",
          words: ["Is", "there", "a", "gym?"],
          a: "Is there a gym?",
          tip: "Singular → is."
        },
        {
          type: "input",
          q: "'Is there a café?' 'Yes, _____ _____.' (2 words)",
          a: "there is",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "mc",
          q: "'Are there any parking spaces?' '___'",
          options: ["Yes, there are.", "Yes, there is.", "Yes, it is."],
          a: "Yes, there are.",
          tip: "Plural question → are.",
          successtip: "Match your answer to the question! Plural=are, Singular=is! 🎯"
        },
        {
          type: "order",
          q: "Order the answer.",
          words: ["No,", "there", "aren't."],
          a: "No, there aren't.",
          tip: "Short negative answer."
        },
        {
          type: "input",
          q: "'Are there any bathrooms?' 'Yes, there _____.' (1 word)",
          a: "are",
          accept: { ignoreCase: true, ignorePunctuation: true }
        }
      ]
    },

    // ===================================================================
    // SET 6: COUNTABLE vs UNCOUNTABLE (A2+)
    // ===================================================================
    {
      id: "m2-u5-set6",
      title: "Set 6: Countable vs Uncountable",
      questions: [
        {
          type: "match",
          q: "Match to countable or uncountable.",
          pairs: [
            { left: "Water", right: "Uncountable (is)" },
            { left: "Chairs", right: "Countable (are)" },
            { left: "Money", right: "Uncountable (is)" }
          ],
          a: "completed",
          successtip: "Countable=are, Uncountable=is! Learn which is which! 💧🪑💰",
          tip: "Uncountable nouns use singular verb!"
        },
        {
          type: "mc",
          q: "'Is there ___ milk?'",
          options: ["any", "some"],
          a: "any",
          tip: "Question → any. Milk = uncountable."
        },
        {
          type: "order",
          q: "Order the sentence.",
          words: ["There", "is", "some", "coffee."],
          a: "There is some coffee.",
          tip: "Coffee = uncountable → is."
        },
        {
          type: "input",
          q: "'There ___ some sugar.' (is/are)",
          a: "is",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Sugar = uncountable."
        },
        {
          type: "mc",
          q: "'There are ___ eggs.'",
          options: ["some", "is"],
          a: "some",
          tip: "Eggs = countable plural."
        },
        {
          type: "order",
          q: "Order the sentence.",
          words: ["There", "isn't", "any", "bread."],
          a: "There isn't any bread.",
          tip: "Bread = uncountable.",
          successtip: "Bread, water, milk, sugar = uncountable! Use IS! 🍞"
        },
        {
          type: "input",
          q: "'Are there ___ apples?' (any/some)",
          a: "any",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Question → any."
        },
        {
          type: "mc",
          q: "'There is ___ water in the bottle.'",
          options: ["some", "any"],
          a: "some",
          tip: "Positive → some. Water = uncountable."
        },
        {
          type: "order",
          q: "Order the sentence.",
          words: ["There", "are", "some", "bottles."],
          a: "There are some bottles.",
          tip: "Bottles = countable plural."
        },
        {
          type: "input",
          q: "'There isn't ___ rice.' (any/some)",
          a: "any",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Negative → any. Rice = uncountable."
        }
      ]
    },

    // ===================================================================
    // SET 7: ERROR CORRECTION (A2+)
    // ===================================================================
    {
      id: "m2-u5-set7",
      title: "Set 7: Fix the Mistakes",
      questions: [
        {
          type: "mc",
          q: "Error: 'Is there any restaurants?'",
          options: ["ARE there any restaurants?", "Is there A restaurant?"],
          a: "ARE there any restaurants?",
          tip: "Restaurants = plural → are.",
          successtip: "Common mistake! Plural noun needs ARE! ⚠️"
        },
        {
          type: "mc",
          q: "Error: 'There is people in the street.'",
          options: ["There ARE people...", "There is A people..."],
          a: "There ARE people...",
          tip: "People = always plural!"
        },
        {
          type: "input",
          q: "Fix: 'There are some milk.' → 'There ___ some milk.'",
          a: "is",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Milk = uncountable → is."
        },
        {
          type: "order",
          q: "Fix: 'There isn't no milk.' (double negative)",
          words: ["There", "isn't", "any", "milk."],
          a: "There isn't any milk.",
          tip: "Remove double negative!",
          successtip: "No double negatives in English! Use isn't + any! ❌❌"
        },
        {
          type: "mc",
          q: "Error: 'I have any questions.' (positive)",
          options: ["I have SOME questions", "I have NO questions"],
          a: "I have SOME questions",
          tip: "Positive → some, NOT any."
        },
        {
          type: "input",
          q: "Fix: 'Are there some chairs?' → 'Are there ___ chairs?'",
          a: "any",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Question → any."
        },
        {
          type: "mc",
          q: "Error: 'There are a dog.'",
          options: ["There IS a dog.", "There are dogs."],
          a: "There IS a dog.",
          tip: "A dog = singular → is."
        },
        {
          type: "order",
          q: "Fix the word order.",
          words: ["There", "are", "many", "trees."],
          a: "There are many trees.",
          tip: "Standard word order."
        },
        {
          type: "input",
          q: "Fix: 'Is there some milk?' → 'Is there ___ milk?'",
          a: "any",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Question → any."
        },
        {
          type: "mc",
          q: "Error: 'There isn't some water.'",
          options: ["There isn't ANY water.", "There is some water."],
          a: "There isn't ANY water.",
          tip: "Negative → any.",
          successtip: "Negative sentences use ANY, not SOME! 🚫"
        }
      ]
    },

    // ===================================================================
    // SET 8: REAL-LIFE SITUATIONS (B1-)
    // ===================================================================
    {
      id: "m2-u5-set8",
      title: "Set 8: Real Conversations",
      questions: [
        {
          type: "match",
          q: "Match the situation to the question.",
          pairs: [
            { left: "At a hotel", right: "Is there a swimming pool?" },
            { left: "At a restaurant", right: "Are there any vegetarian options?" },
            { left: "Looking for parking", right: "Is there a parking lot?" }
          ],
          a: "completed",
          successtip: "Real-life survival questions! Use them! 🏨🍽️🅿️"
        },
        {
          type: "mc",
          q: "At hotel reception: 'Excuse me, ___ a gym?'",
          options: ["is there", "are there"],
          a: "is there",
          tip: "A gym = singular."
        },
        {
          type: "order",
          q: "At a store: Ask about bathrooms.",
          words: ["Is", "there", "a", "bathroom?"],
          a: "Is there a bathroom?",
          tip: "Polite question."
        },
        {
          type: "input",
          q: "At restaurant: 'Are there ___ tables available?'",
          a: "any",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Question → any."
        },
        {
          type: "mc",
          q: "Looking for wifi: '___'",
          options: ["Is there wifi?", "Are there wifi?"],
          a: "Is there wifi?",
          tip: "Wifi = uncountable → is.",
          successtip: "Is there wifi? Essential question everywhere! 📶"
        },
        {
          type: "order",
          q: "At apartment viewing: Ask about parking.",
          words: ["Is", "there", "parking?"],
          a: "Is there parking?",
          tip: "Parking = uncountable."
        },
        {
          type: "input",
          q: "At café: 'Are there ___ vegan options?'",
          a: "any",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "mc",
          q: "At store: '___'",
          options: ["Is there a sale?", "Are there a sale?"],
          a: "Is there a sale?",
          tip: "A sale = singular."
        },
        {
          type: "order",
          q: "At tourist office: Ask about museums.",
          words: ["Are", "there", "any", "museums?"],
          a: "Are there any museums?",
          tip: "Museums = plural."
        },
        {
          type: "input",
          q: "At hotel: 'Is there ___ breakfast included?'",
          a: "a",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["any"] },
          tip: "Can use 'a' or leave it out."
        }
      ]
    },

    // ===================================================================
    // SET 9: PREPOSITIONS OF PLACE (B1-)
    // ===================================================================
    {
      id: "m2-u5-set9",
      title: "Set 9: Where is it?",
      questions: [
        {
          type: "match",
          q: "Match the preposition to the meaning.",
          pairs: [
            { left: "On", right: "Top of surface" },
            { left: "In", right: "Inside" },
            { left: "Under", right: "Below" },
            { left: "Next to", right: "Beside" }
          ],
          a: "completed",
          successtip: "Location words! On=sobre, In=dentro, Under=debajo! 📍"
        },
        {
          type: "mc",
          q: "'There is a lamp ___ the table.'",
          options: ["on", "in", "at"],
          a: "on",
          tip: "On the table = on top of."
        },
        {
          type: "order",
          q: "Order the sentence.",
          words: ["There", "is", "a", "cat", "under", "the", "bed."],
          a: "There is a cat under the bed.",
          tip: "Under = debajo de."
        },
        {
          type: "input",
          q: "'There are books ___ the shelf.' (on/in)",
          a: "on",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "On the shelf."
        },
        {
          type: "mc",
          q: "'The sofa is ___ the living room.'",
          options: ["in", "on", "at"],
          a: "in",
          tip: "In a room.",
          successtip: "IN rooms, ON surfaces, AT locations! 🏠"
        },
        {
          type: "order",
          q: "Order the sentence.",
          words: ["There", "is", "a", "picture", "on", "the", "wall."],
          a: "There is a picture on the wall.",
          tip: "On the wall."
        },
        {
          type: "input",
          q: "'There is a dog ___ the house.' (inside)",
          a: "in",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "mc",
          q: "'The bank is ___ the post office.' (beside)",
          options: ["next to", "in", "under"],
          a: "next to",
          tip: "Next to = beside."
        },
        {
          type: "order",
          q: "Order the sentence.",
          words: ["There", "is", "a", "rug", "under", "the", "table."],
          a: "There is a rug under the table.",
          tip: "Under = debajo."
        },
        {
          type: "input",
          q: "'The restaurant is ___ Main Street.'",
          a: "on",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "On a street.",
          successtip: "ON streets and avenues! 🛣️"
        }
      ]
    },

    // ===================================================================
    // SET 10: MIXED REVIEW (B1-)
    // ===================================================================
    {
      id: "m2-u5-set10",
      title: "Set 10: Master Challenge",
      questions: [
        {
          type: "match",
          q: "Match the sentence type.",
          pairs: [
            { left: "There is a...", right: "Singular (+)" },
            { left: "There are some...", right: "Plural (+)" },
            { left: "There isn't any...", right: "Negative" },
            { left: "Is there any...?", right: "Question" }
          ],
          a: "completed",
          successtip: "Master the patterns! You know them all! 🎯"
        },
        {
          type: "order",
          q: "Translate: Hay muchas personas.",
          words: ["There", "are", "a", "lot", "of", "people."],
          a: "There are a lot of people.",
          tip: "People = plural."
        },
        {
          type: "mc",
          q: "'Montreal is great. There ___ a lot of festivals.'",
          options: ["are", "is"],
          a: "are",
          tip: "Festivals = plural."
        },
        {
          type: "input",
          q: "Translate: No hay problema. (3 words)",
          a: "There's no problem",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["There is no problem", "No problem"] },
          tip: "Multiple ways to say this!"
        },
        {
          type: "order",
          q: "Translate: No hay coches en la calle.",
          words: ["There", "aren't", "any", "cars", "on", "the", "street."],
          a: "There aren't any cars on the street.",
          tip: "Negative plural."
        },
        {
          type: "match",
          q: "Match quantities.",
          pairs: [
            { left: "100+", right: "A lot of" },
            { left: "5-10", right: "Some" },
            { left: "0", right: "Any/None" }
          ],
          a: "completed",
          successtip: "Quantity scale mastered! 📊"
        },
        {
          type: "mc",
          q: "'Is there ___ water?'",
          options: ["any", "some", "a"],
          a: "any",
          tip: "Question + uncountable → any."
        },
        {
          type: "input",
          q: "'There ___ many options.' (is/are)",
          a: "are",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Options = plural."
        },
        {
          type: "order",
          q: "Make a question: bathroom / here",
          words: ["Is", "there", "a", "bathroom", "here?"],
          a: "Is there a bathroom here?",
          tip: "Useful question!"
        },
        {
          type: "mc",
          q: "Final question! How do you feel about There is/are?",
          options: ["I understand it!", "I need more practice", "It's confusing"],
          a: "I understand it!",
          successtip: "Perfect! You've mastered There is/are & Quantifiers! Unit 5 complete! 🎉"
        }
      ]
    }
  ],

  // ===================================================================
  // REVIEW SET (10 questions)
  // ===================================================================
  reviewSet: {
    id: "m2-u5-review",
    title: "Unit 5 Review",
    questions: [
      {
        type: "mc",
        q: "Review: 'There ___ a park.'",
        options: ["is", "are"],
        a: "is",
        tip: "Singular → is."
      },
      {
        type: "input",
        q: "Review: 'There are ___ chairs.' (positive)",
        a: "some",
        accept: { ignoreCase: true, ignorePunctuation: true }
      },
      {
        type: "mc",
        q: "Review: 'There aren't ___ hotels.'",
        options: ["any", "some"],
        a: "any",
        tip: "Negative → any."
      },
      {
        type: "order",
        q: "Review: Order the question.",
        words: ["Is", "there", "a", "bank?"],
        a: "Is there a bank?",
        tip: "Question form."
      },
      {
        type: "input",
        q: "Review: 'There are ___ ___ ___ people.' (many - 3 words)",
        a: "a lot of",
        accept: { ignoreCase: true, ignorePunctuation: true }
      },
      {
        type: "mc",
        q: "Review: 'Is there ___ milk?'",
        options: ["any", "some"],
        a: "any",
        tip: "Question → any."
      },
      {
        type: "order",
        q: "Review: Order the sentence.",
        words: ["There", "is", "some", "coffee."],
        a: "There is some coffee.",
        tip: "Positive uncountable."
      },
      {
        type: "input",
        q: "Review: 'There ___ many trees.' (is/are)",
        a: "are",
        accept: { ignoreCase: true, ignorePunctuation: true },
        tip: "Trees = plural."
      },
      {
        type: "mc",
        q: "Review: Short answer: 'Are there shops?' '___'",
        options: ["Yes, there are.", "Yes, there is."],
        a: "Yes, there are.",
        tip: "Match the question."
      },
      {
        type: "order",
        q: "Review: Final review!",
        words: ["There", "aren't", "any", "problems."],
        a: "There aren't any problems.",
        successtip: "Excellent review! Ready for reading! 📖"
      }
    ]
  },

  // ===================================================================
  // READING SET (10 questions based on passage)
  // ===================================================================
  readingSet: {
    id: "m2-u5-reading",
    title: "Reading: My Neighborhood",
    passage: `
**MY NEIGHBORHOOD**

I live in a great neighborhood in the city. There are a lot of shops and restaurants on my street. There is a small park next to my building where people walk their dogs.

On the corner, there is a café that I love. They have delicious coffee and some amazing cakes. There isn't a supermarket very close, but there is one about 10 minutes away by bus.

In my apartment building, there are 20 apartments. There is an elevator, which is very helpful! There are also some parking spaces in the back. Unfortunately, there isn't a gym in the building, but there are three gyms in the neighborhood.

My apartment is on the third floor. There is a living room, a bedroom, a kitchen, and a bathroom. In the kitchen, there are some modern appliances and a lot of cupboards. In the bedroom, there is a big bed and a wardrobe. There are two windows with a nice view of the park.

The neighborhood is very convenient. There is a metro station two blocks away, and there are many bus stops. There aren't any hospitals very close, but there is a small clinic on the next street. There are also some schools, which makes it a good area for families.

I really like living here. There are a lot of trees, the people are friendly, and there is always something to do!
    `,
    questions: [
      {
        type: "mc",
        q: "What is on the writer's street?",
        options: ["Shops and restaurants", "Only houses", "A hospital"],
        a: "Shops and restaurants",
        tip: "First paragraph mentions this."
      },
      {
        type: "input",
        q: "What is next to the building?",
        a: "park",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["a park", "a small park"] },
        tip: "Look for 'next to my building.'"
      },
      {
        type: "mc",
        q: "Is there a supermarket very close?",
        options: ["No, there isn't", "Yes, there is"],
        a: "No, there isn't",
        tip: "The passage says 'There isn't...'"
      },
      {
        type: "input",
        q: "How many apartments are in the building?",
        a: "20",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["twenty", "20 apartments"] },
        successtip: "20 apartments! 🏢"
      },
      {
        type: "mc",
        q: "Is there a gym in the building?",
        options: ["No, there isn't", "Yes, there is"],
        a: "No, there isn't",
        tip: "'Unfortunately, there isn't...'"
      },
      {
        type: "input",
        q: "How many gyms are in the neighborhood?",
        a: "three",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["3", "three gyms"] },
        tip: "'There are three gyms...'"
      },
      {
        type: "mc",
        q: "What floor is the apartment on?",
        options: ["Third floor", "Second floor", "First floor"],
        a: "Third floor",
        successtip: "Third floor with a park view! 🌳"
      },
      {
        type: "input",
        q: "How many windows are in the bedroom?",
        a: "two",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["2", "two windows"] },
        tip: "'There are two windows...'"
      },
      {
        type: "mc",
        q: "Is there a metro station nearby?",
        options: ["Yes, there is", "No, there isn't"],
        a: "Yes, there is",
        tip: "'Two blocks away' = nearby."
      },
      {
        type: "mc",
        q: "What makes the neighborhood good for families?",
        options: ["There are schools", "There's a hospital", "There are gyms"],
        a: "There are schools",
        tip: "Last paragraph mentions schools.",
        successtip: "Perfect! Unit 5 complete! You're a There is/are expert! 🏆"
      }
    ]
  }
};
